import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/axios";

const MetaContext = createContext();

const DEFAULT_TEAMS = [
  { _id: "software", name: "Software", value: "software" },
  { _id: "embedded", name: "Embedded", value: "embedded" },
  { _id: "designteam", name: "Design Team", value: "designteam" },
  { _id: "hr", name: "HR", value: "hr" },
  { _id: "finance", name: "Finance", value: "finance" },
  { _id: "manufacturing", name: "Manufacturing", value: "manufacturing" },
  { _id: "sales team", name: "Sales Team", value: "sales team" },
  { _id: "tele marketing", name: "Tele Marketing", value: "tele marketing" },
  { _id: "digital marketing", name: "Digital Marketing", value: "digital marketing" },
];

const DEFAULT_ROLES = ["intern", "junior", "senior", "lead", "hr", "finance"];

const normalize = (s) => String(s || "").trim().toLowerCase();

const mergeUniqueByName = (dbTeams = []) => {
  const merged = [...dbTeams];
  DEFAULT_TEAMS.forEach((def) => {
    if (!merged.some((t) => normalize(t.value) === normalize(def.value))) {
      merged.push(def);
    }
  });
  return merged;
};

export const MetaProvider = ({ children }) => {
  const [teams, setTeams] = useState(DEFAULT_TEAMS);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [logoUrl, setLogoUrl] = useState(null);

  const refreshLogo = useCallback(async () => {
    try {
      const res = await api.get("/api/settings");
      setLogoUrl(res.data?.logo_url || null);
    } catch (e) {
      // ignore
    }
  }, []);

  const refreshTeams = useCallback(async () => {
    try {
      const res = await api.get("/api/teams");
      const dbTeams = Array.isArray(res.data)
        ? res.data.map((t) => ({
            _id: t._id,
            name: t.name,
            value: t.name,
          }))
        : [];
      setTeams(mergeUniqueByName(dbTeams));
    } catch (e) {
      // ignore unauth errors on public pages
    }
  }, []);

  const refreshRoles = useCallback(async () => {
    try {
      const res = await api.get("/api/roles");
      const list = Array.isArray(res.data) ? res.data : [];
      const merged = Array.from(
        new Set([...DEFAULT_ROLES, ...list].map((r) => normalize(r)).filter(Boolean)),
      );
      setRoles(merged);
    } catch (e) {
      // ignore
    }
  }, []);

  const addTeam = useCallback(async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) throw new Error("Team name required");
    await api.post("/api/admin/teams", { name: trimmed });
    await refreshTeams();
    return trimmed;
  }, [refreshTeams]);

  const addRole = useCallback(async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) throw new Error("Role name required");
    const res = await api.post("/api/admin/roles", { name: trimmed });
    const list = Array.isArray(res.data) ? res.data : [];
    const merged = Array.from(
      new Set([...DEFAULT_ROLES, ...list].map((r) => normalize(r)).filter(Boolean)),
    );
    setRoles(merged);
    return normalize(trimmed);
  }, []);

  const deleteTeam = useCallback(async (teamId) => {
    if (!teamId) throw new Error("Team id required");
    await api.delete(`/api/admin/teams/${teamId}`);
    await refreshTeams();
  }, [refreshTeams]);

  const deleteRole = useCallback(async (name) => {
    const roleName = normalize(name);
    if (!roleName) throw new Error("Role name required");
    await api.delete(`/api/admin/roles/${roleName}`);
    await refreshRoles();
  }, [refreshRoles]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      refreshTeams();
      refreshRoles();
      refreshLogo();
    }
  }, [refreshTeams, refreshRoles, refreshLogo]);

  const value = useMemo(
    () => ({ teams, roles, logoUrl, refreshTeams, refreshRoles, refreshLogo, addTeam, addRole, deleteTeam, deleteRole }),
    [teams, roles, logoUrl, refreshTeams, refreshRoles, refreshLogo, addTeam, addRole, deleteTeam, deleteRole],
  );

  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>;
};

export const useMeta = () => useContext(MetaContext);
